import '@tempots/beatui/css'
import { BeatUI } from '@tempots/beatui'
import { ChildRouter, NavigationService, RootRouter } from '@tempots/ui'
import { RequireAgencyMembership } from './components/require-agency-membership'
import { HomePage } from './pages/home'
import { CookieConsentBanner } from './components/cookie-consent-banner'
import { WorkspacePage } from './pages/workspace'
import { TrialPage } from './pages/trial'
import { SignupPage } from './pages/signup'
import { CustomizePage } from './pages/customize/customize-page'
import { ProfilePage } from './pages/profile'
import { AgencyPage } from './pages/agency'
import { BillingPage } from './pages/billing'
import { AboutPage } from './pages/about'
import { ContactPage } from './pages/contact'
import { TermsPage } from './pages/terms'
import { PrivacyPage } from './pages/privacy'
import { OnboardingPage } from './pages/onboarding'
import { AgencyOnboardingPage } from './pages/agency-onboarding'
import { NotFoundPage } from './pages/not-found-page'
import { Empty } from '@tempots/dom'
import { ConfirmEmailPage } from './pages/confirm-email'
import { VerifyEmailPage } from './pages/verify-email'
import { StartPage } from './pages/start'
import { SignInPage } from './pages/signin'
import { ForgotPasswordPage } from './pages/forgot-password'
import { ResetPasswordPage } from './pages/reset-password'
import { AgencyService } from './services/agency-service'
import { Auth } from './services/auth-service'
import { DevEnvRibbon } from '@rh/shared'
import { FaqPage } from './pages/faq'
import { Track } from './services/track'

export const App = () => {
  let resumeFile: File | null = null
  let trialPageUploadCallback: ((file: File | null) => void) | null = null

  const setResumeFile = (file: File | null) => {
    resumeFile = file
    if (file == null) {
      return
    }
    if (
      Auth.authReady.value &&
      Auth.isSignedIn.value &&
      AgencyService.preferredAgency.value != null
    ) {
      if (
        location.pathname !==
        `/${AgencyService.preferredAgency.value}/workspace`
      ) {
        NavigationService.navigate(
          `/${AgencyService.preferredAgency.value}/workspace`
        )
      }
      return
    } else {
      // If already on trial page and callback is registered, trigger upload directly
      if (location.pathname === '/trial' && trialPageUploadCallback) {
        trialPageUploadCallback(file)
        return
      }
      // Otherwise navigate to trial page
      NavigationService.navigate('/trial')
    }
  }

  const registerTrialPageCallback = (callback: (file: File | null) => void) => {
    trialPageUploadCallback = callback
  }

  return BeatUI(
    {
      includeAuthI18n: true,
      enableAppearance: false,
      includeNotifications: true,
    },
    DevEnvRibbon(),
    CookieConsentBanner(),
    RootRouter({
      '/*': info => {
        // Page Navigation tracking
        info.$.path.on(Track.navigation)
        return Empty
      },
    }),
    RootRouter({
      '/': () => HomePage({ setResumeFile }),
      '/about': AboutPage,
      '/contact': ContactPage,
      '/terms': TermsPage,
      '/faq': FaqPage,
      '/privacy': PrivacyPage,
      '/trial': () =>
        TrialPage({
          resumeFile,
          setResumeFile,
          onResumeFileChange: registerTrialPageCallback,
        }),
      '/signup': SignupPage,
      '/signin': SignInPage,
      '/forgot-password': ForgotPasswordPage,
      '/reset-password': ResetPasswordPage,
      '/confirm-email': ConfirmEmailPage,
      '/verify-email': VerifyEmailPage,
      '/onboarding': OnboardingPage,
      '/start': StartPage,
      '/:agencyId/*': info => {
        const agencyId = info.$.params.$.agencyId
        agencyId.on(id => AgencyService.updatePreferredAgency(id))
        // Non-members of the URL's agency (e.g. removed while it was
        // their active one) are rerouted to /start instead of hitting
        // membership errors on every page.
        return RequireAgencyMembership({ agencyId }, () =>
          ChildRouter({
            '/workspace': () =>
              WorkspacePage({ agencyId, setResumeFile, resumeFile }),
            '/customize': () => CustomizePage({ agencyId }),
            '/profile': () => ProfilePage({ agencyId }),
            '/agency': () => AgencyPage({ agencyId }),
            // Legacy segment from when this section was called "Team"
            '/team': () => AgencyPage({ agencyId }),
            '/billing': () => BillingPage({ agencyId }),
            '/onboarding': () => AgencyOnboardingPage({ agencyId }),
            '/*': () => NotFoundPage(),
          })
        )
      },

      '/*': () => NotFoundPage(),
    })
  )
}
